import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Check, ShieldAlert, Users, HeartHandshake, User as UserIcon } from 'lucide-react';

const GOALS = [
  'Reduce stress', 'Improve mental wellness', 'Manage anxiety', 'Improve relationships',
  'Sleep better', 'Become a better father', 'Build purpose', 'Improve finances',
  'Grow spiritually', 'Improve emotional control',
];
const STYLES = ['Practical', 'Encouraging', 'Faith-informed', 'Direct', 'Reflective'];

const CULTURAL_TOPICS = [
  'Navigating racism', 'Code-switching', 'Being the only one in the room',
  'Cultural expectations of masculinity', 'Colorism', 'Intergenerational expectations',
  'Mental-health stigma in my community', 'Success & survivor\u2019s guilt',
  'Cultural identity', 'Discrimination at work', 'Immigration & belonging',
];
const ETHNICITIES = [
  { id: 'black', label: 'Black / African American' },
  { id: 'latino', label: 'Latino / Hispanic' },
  { id: 'asian', label: 'Asian' },
  { id: 'mena', label: 'Middle Eastern / North African' },
  { id: 'indigenous', label: 'Indigenous / Native' },
  { id: 'white', label: 'White' },
  { id: 'multiracial', label: 'Multiracial' },
  { id: 'other', label: 'Another background' },
  { id: 'prefer_not', label: 'Prefer not to say' },
];

/**
 * Support-relationship options (Brotherhood Support role).
 * Answers the question: "Who are you here to support?"
 */
const SUPPORT_RELATIONSHIPS = [
  { id: 'partner',   label: 'A partner or spouse' },
  { id: 'brother',   label: 'A brother' },
  { id: 'father',    label: 'A father or father figure' },
  { id: 'son',       label: 'A son' },
  { id: 'friend',    label: 'A close friend' },
  { id: 'family',    label: 'Another family member' },
  { id: 'colleague', label: 'A colleague or mentee' },
  { id: 'community', label: 'Community members I lead or serve' },
  { id: 'multiple',  label: 'Multiple men in my life' },
  { id: 'general',   label: 'Just here to learn how to support men' },
];

/**
 * Member reasons — added for parity with supporters. Optional; helps segment.
 */
const MEMBER_REASONS = [
  { id: 'self-growth',  label: 'General personal growth' },
  { id: 'stress',       label: 'Managing stress or anxiety' },
  { id: 'relationship', label: 'Strengthening a relationship' },
  { id: 'fatherhood',   label: 'Being a better father' },
  { id: 'purpose',      label: 'Finding purpose or direction' },
  { id: 'grief',        label: 'Working through grief or loss' },
  { id: 'community',    label: 'Connecting with other men' },
  { id: 'crisis',       label: 'I\'m in a hard season and need support' },
];

const currentYear = new Date().getFullYear();
// Show years back to age 100. Users who pick an under-18 year get a friendly
// eligibility message rather than being silently blocked from the dropdown.
const YEAR_LIST = Array.from({ length: 101 }, (_, i) => currentYear - i);

export default function Onboarding() {
  const { refresh, user, logout } = useAuth();
  const nav = useNavigate();
  const isSupporter = user?.role === 'supporter';

  const exitToSignIn = async () => {
    try { await logout(); } catch (err) { console.debug('[onboarding] logout failed (non-fatal):', err); }
    nav('/login');
  };

  // Step 0: birth year (age gate)
  // Step 1: reason / relationship
  // Step 2 (member only): support style
  const [step, setStep] = useState(0);
  const [birthYear, setBirthYear] = useState('');
  const [ageOk, setAgeOk] = useState(null);   // null = untouched, false = under 18, true = passed
  const [reason, setReason] = useState(null); // one member reason OR supporter relationship id
  const [goals, setGoals] = useState([]);
  const [style, setStyle] = useState(null);
  const [faith, setFaith] = useState(false);
  const [culturalTopics, setCulturalTopics] = useState([]);
  const [ethnicity, setEthnicity] = useState('');
  const [ackAdvice, setAckAdvice] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = isSupporter ? 2 : 3;

  const toggleGoal = (g) => setGoals(goals.includes(g) ? goals.filter(x => x !== g) : [...goals, g]);
  const toggleCultural = (t) => setCulturalTopics(culturalTopics.includes(t) ? culturalTopics.filter(x => x !== t) : [...culturalTopics, t]);

  const validateAge = () => {
    const y = parseInt(birthYear, 10);
    if (!y || y < 1900 || y > currentYear) { setAgeOk(false); return false; }
    const age = currentYear - y;
    const ok = age >= 18;
    setAgeOk(ok);
    return ok;
  };

  const submit = async () => {
    if (!validateAge()) return;
    setSubmitting(true);
    try {
      await http.post('/auth/onboarding', {
        goals,
        support_style: style,
        faith_enabled: !!faith || style === 'Faith-informed',
        birth_year: parseInt(birthYear, 10),
        support_context: reason,
        growth_topics: [...goals, ...culturalTopics],
        ethnicity: (ETHNICITIES.find(e => e.id === ethnicity)?.label) || null,
      });
      await refresh();
      toast.success('You\'re all set');
      nav(isSupporter ? '/app/support-home' : '/assessment');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save preferences');
    } finally {
      setSubmitting(false);
    }
  };

  const options = useMemo(() => isSupporter ? SUPPORT_RELATIONSHIPS : MEMBER_REASONS, [isSupporter]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-6 flex items-center">
      <div className="w-full max-w-md mx-auto bg-white rounded-3xl p-8 card-soft animate-fade-up">
        {/* Exit */}
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-2">
            <span className="w-8 h-8 rounded-xl bg-navy text-white flex items-center justify-center font-heading text-sm">B<span className="text-gold">+</span></span>
            <span className="font-heading text-lg tracking-tight text-navy bh-goldshine">Brotherhood<span className="text-gold">+</span></span>
          </span>
          <button
            data-testid="onboarding-exit"
            onClick={exitToSignIn}
            className="text-xs text-navy/45 hover:text-navy underline underline-offset-4"
          >
            Exit to sign in
          </button>
        </div>
        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-navy' : 'bg-navy/10'}`}/>
          ))}
        </div>

        {/* ── Step 0: Age gate ── */}
        {step === 0 && (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">Welcome</p>
            <h1 className="font-heading text-3xl mb-2">First — how old are you?</h1>
            <p className="text-navy/60 text-sm mb-6">Brotherhood+ is an 18+ experience. We keep the space honest and adult-only.</p>

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-navy/60">Year of birth</span>
              <select
                data-testid="onb-birth-year"
                value={birthYear}
                onChange={(e) => { setBirthYear(e.target.value); setAgeOk(null); }}
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value="">Select a year…</option>
                {YEAR_LIST.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>

            {ageOk === false && (
              <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100 text-red-800 text-sm" data-testid="onb-age-fail">
                <ShieldAlert size={16} className="mt-0.5 shrink-0"/>
                <span>You must be at least 18 to use Brotherhood+. If you&apos;re under 18, please talk to a trusted adult — you can also call or text <b>988</b> (US) any time you need to talk.</span>
              </div>
            )}

            <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="onb-ack-label">
              <input
                type="checkbox"
                data-testid="onb-ack-advice"
                checked={ackAdvice}
                onChange={(e) => setAckAdvice(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-navy/30 text-navy focus:ring-gold accent-navy"
              />
              <span className="text-sm text-navy/70 leading-relaxed">
                I understand Brotherhood+ is for education, wellness, and self-reflection — it is <span className="font-semibold text-navy">not professional, medical, or therapeutic advice</span>, and is not a substitute for care from a licensed clinician.
              </span>
            </label>

            <button
              data-testid="onb-next-0"
              onClick={() => { if (ackAdvice && validateAge()) setStep(1); }}
              disabled={!birthYear || !ackAdvice}
              className="mt-6 w-full bg-navy text-white rounded-full py-4 font-medium hover:bg-navy-soft transition-smooth disabled:opacity-40">
              Continue
            </button>
            <p className="text-[11px] text-navy/40 text-center mt-3">
              We use this only to confirm eligibility. It&apos;s never shared.
            </p>
          </>
        )}

        {/* ── Step 1: Reason (member) or Relationship (supporter) ── */}
        {step === 1 && (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">
              Step 1 of {totalSteps - 1}
            </p>
            {isSupporter ? (
              <>
                <h1 className="font-heading text-3xl mb-2 flex items-center gap-2">
                  <HeartHandshake size={22} className="text-gold" strokeWidth={1.75}/> Who are you here to support?
                </h1>
                <p className="text-navy/60 text-sm mb-6">Pick the one that fits best. You can add more men to your circle later.</p>
              </>
            ) : (
              <>
                <h1 className="font-heading text-3xl mb-2 flex items-center gap-2">
                  <UserIcon size={22} className="text-gold" strokeWidth={1.75}/> What brought you here?
                </h1>
                <p className="text-navy/60 text-sm mb-6">One quick answer — it helps us tailor your experience.</p>
              </>
            )}

            <div className="space-y-2 mb-6 max-h-[46vh] overflow-y-auto pr-1">
              {options.map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setReason(o.id)}
                  data-testid={`onb-reason-${o.id}`}
                  className={`w-full text-left p-4 rounded-2xl border transition-smooth ${
                    reason === o.id ? 'bg-navy text-white border-navy' : 'bg-beige border-transparent hover:bg-beige/70'
                  }`}>
                  <span className="flex items-center gap-2 text-sm font-medium">
                    {reason === o.id && <Check size={14}/>}
                    {o.label}
                  </span>
                </button>
              ))}
            </div>

            {isSupporter ? (
              // Supporters finish here — 2 steps total for them.
              <button
                data-testid="onb-finish"
                disabled={!reason || submitting}
                onClick={submit}
                className="w-full bg-gold text-white rounded-full py-4 font-medium hover:bg-gold-hover transition-smooth disabled:opacity-40">
                {submitting ? 'Saving…' : 'Finish setup'}
              </button>
            ) : (
              <button
                data-testid="onb-next-1"
                disabled={!reason}
                onClick={() => setStep(2)}
                className="w-full bg-navy text-white rounded-full py-4 font-medium hover:bg-navy-soft transition-smooth disabled:opacity-40">
                Continue
              </button>
            )}
          </>
        )}

        {/* ── Step 2 (member only): support style + optional goals ── */}
        {step === 2 && !isSupporter && (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">Step 2 of {totalSteps - 1}</p>
            <h1 className="font-heading text-3xl mb-2 flex items-center gap-2">
              <Users size={22} className="text-gold" strokeWidth={1.75}/> How do you want to be supported?
            </h1>
            <p className="text-navy/60 text-sm mb-6">Choose one that fits best.</p>
            <div className="space-y-2 mb-4">
              {STYLES.map(s => (
                <button
                  key={s}
                  type="button"
                  data-testid={`style-${s}`}
                  onClick={() => setStyle(s)}
                  className={`w-full text-left p-4 rounded-2xl border transition-smooth ${
                    style === s ? 'bg-navy text-white border-navy' : 'bg-beige border-transparent hover:bg-beige/70'
                  }`}>{s}</button>
              ))}
            </div>

            {/* Quick multi-select of specific goals (optional) */}
            <p className="text-xs uppercase tracking-widest text-navy/50 mt-4 mb-2">Optional focus areas</p>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {GOALS.map(g => (
                <button
                  key={g}
                  type="button"
                  data-testid={`goal-${g}`}
                  onClick={() => toggleGoal(g)}
                  className={`text-left text-xs p-2.5 rounded-xl border transition-smooth ${
                    goals.includes(g) ? 'bg-navy text-white border-navy' : 'bg-beige border-transparent'
                  }`}>
                  <span className="flex items-center gap-1.5">
                    {goals.includes(g) && <Check size={12}/>}{g}
                  </span>
                </button>
              ))}
            </div>

            {/* Optional ethnicity — tailors the AI Coach's cultural context (never required) */}
            <p className="text-xs uppercase tracking-widest text-navy/50 mt-4 mb-1">Your background <span className="normal-case tracking-normal text-navy/40">(optional)</span></p>
            <p className="text-[11px] text-navy/45 mb-2 leading-relaxed">Helps your Coach speak to your lived experience. You can skip this — it&apos;s never shared.</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {ETHNICITIES.map(e => (
                <button
                  key={e.id}
                  type="button"
                  data-testid={`onb-ethnicity-${e.id}`}
                  onClick={() => setEthnicity(ethnicity === e.id ? '' : e.id)}
                  className={`text-xs px-3 py-2 rounded-full border transition-smooth ${
                    ethnicity === e.id ? 'bg-navy text-white border-navy' : 'bg-beige border-transparent hover:bg-beige/70'
                  }`}>
                  <span className="flex items-center gap-1.5">
                    {ethnicity === e.id && <Check size={12}/>}{e.label}
                  </span>
                </button>
              ))}
            </div>

            {/* Optional culturally-specific growth topics */}
            <p className="text-xs uppercase tracking-widest text-navy/50 mb-1">Culturally-specific topics <span className="normal-case tracking-normal text-navy/40">(optional)</span></p>
            <p className="text-[11px] text-navy/45 mb-2 leading-relaxed">Pick anything you&apos;d like your Coach to understand about your world.</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {CULTURAL_TOPICS.map(t => (
                <button
                  key={t}
                  type="button"
                  data-testid={`onb-cultural-${t}`}
                  onClick={() => toggleCultural(t)}
                  className={`text-xs px-3 py-2 rounded-full border transition-smooth ${
                    culturalTopics.includes(t) ? 'bg-gold text-white border-gold' : 'bg-beige border-transparent hover:bg-beige/70'
                  }`}>
                  <span className="flex items-center gap-1.5">
                    {culturalTopics.includes(t) && <Check size={12}/>}{t}
                  </span>
                </button>
              ))}
            </div>

            <button
              data-testid="onb-finish"
              disabled={!style || submitting}
              onClick={submit}
              className="w-full bg-gold text-white rounded-full py-4 font-medium hover:bg-gold-hover transition-smooth disabled:opacity-40">
              {submitting ? 'Saving…' : 'Finish setup'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
